import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useConflict, usePatchConflict } from "@/hooks/use-conflicts";
import { cn } from "@/lib/utils";

interface ConflictDetailSheetProps {
  conflictId: string | null;
  solutionId: string;
  onOpenChange: (open: boolean) => void;
}

export function ConflictDetailSheet({
  conflictId,
  solutionId,
  onOpenChange,
}: ConflictDetailSheetProps) {
  const isOpen = conflictId !== null;
  const { data, isLoading } = useConflict(conflictId);
  const patch = usePatchConflict(solutionId);
  const { solutionId: routeSolutionId } = useParams({ from: "/s/$solutionId" });

  const [draft, setDraft] = useState("");
  useEffect(() => {
    if (data) setDraft(data.resolutionNote ?? "");
  }, [data]);

  function resolve() {
    if (!data) return;
    patch.mutate({
      id: data.id,
      patch: { status: "resolved", resolutionNote: draft.trim() || null },
    });
  }
  function reopen() {
    if (!data) return;
    patch.mutate({ id: data.id, patch: { status: "open" } });
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto">
        {isLoading && (
          <div className="p-6 space-y-3">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-32 w-full mt-4" />
          </div>
        )}
        {data && (
          <>
            <SheetHeader className="border-b border-[var(--color-border)]">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                {data.externalKey && (
                  <span className="font-mono tabular-nums">{data.externalKey}</span>
                )}
                <span>·</span>
                <span>{new Date(data.conflictDate).toLocaleDateString()}</span>
                <span
                  className={cn(
                    "ml-auto inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
                    data.status === "resolved"
                      ? "bg-success-bg text-success"
                      : "bg-warning-bg text-warning",
                  )}
                >
                  {data.status === "resolved" ? "Resolved" : "Open"}
                </span>
              </div>
              <SheetTitle className="font-display text-base leading-snug mt-2">
                {data.description}
              </SheetTitle>
              <SheetDescription className="sr-only">Conflict detail</SheetDescription>
            </SheetHeader>

            <div className="p-6 space-y-5">
              <section>
                <h3 className="font-display text-sm font-semibold mb-2">Positions</h3>
                {data.positions.length > 0 ? (
                  <ul className="space-y-2">
                    {data.positions.map((p) => (
                      <li key={p.id} className="rounded-md border border-[var(--color-border)] p-3">
                        <div className="flex items-center gap-2 mb-1">
                          {p.party && (
                            <>
                              <span
                                className={cn(
                                  "inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold",
                                  p.party.org === "client"
                                    ? "bg-nymbl-sky text-nymbl-navy"
                                    : "bg-nymbl-navy text-white",
                                )}
                              >
                                {p.party.initials}
                              </span>
                              <span className="text-xs font-medium">{p.party.name}</span>
                              <span className="text-[10px] text-muted-foreground">
                                {p.party.role}
                              </span>
                            </>
                          )}
                        </div>
                        <p className="text-sm text-foreground/85 italic">“{p.stance}”</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">No positions captured.</p>
                )}
              </section>

              {data.sources.length > 0 && (
                <section>
                  <h3 className="font-display text-sm font-semibold mb-2">Source evidence</h3>
                  <ul className="space-y-1.5">
                    {data.sources.map((s) => (
                      <li
                        key={s.id}
                        className="text-sm text-foreground/80 px-3 py-1.5 rounded border border-[var(--color-border)]"
                      >
                        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mr-2">
                          {s.type}
                        </span>
                        {s.name}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {data.relatedItemIds.length > 0 && (
                <section>
                  <h3 className="font-display text-sm font-semibold mb-2">Related items</h3>
                  <ul className="space-y-1">
                    {data.relatedItemIds.map((id) => (
                      <li key={id}>
                        <Link
                          to="/s/$solutionId/review/$itemId"
                          params={{ solutionId: routeSolutionId, itemId: id }}
                          className="text-sm text-nymbl-blue hover:underline"
                        >
                          {id.slice(0, 8)}…
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section>
                <h3 className="font-display text-sm font-semibold mb-2">Resolution note</h3>
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="What was decided and why…"
                  rows={4}
                  className="text-sm"
                />
                <div className="flex gap-2 mt-2">
                  {data.status === "open" ? (
                    <Button size="sm" onClick={resolve} disabled={patch.isPending}>
                      Mark resolved
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={reopen} disabled={patch.isPending}>
                      Reopen
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      patch.mutate({
                        id: data.id,
                        patch: { resolutionNote: draft.trim() || null },
                      })
                    }
                    disabled={patch.isPending || draft === (data.resolutionNote ?? "")}
                  >
                    Save note only
                  </Button>
                </div>
              </section>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
